import L from 'leaflet';
import React, { useCallback, useMemo, useState } from 'react';
import { Marker, Polygon, Tooltip } from 'react-leaflet';
import useGeometryEditing from './hooks/useGeometryEditing';

// Default half-width/height for seed polygon; doubled for a roomier initial shape.
const DEFAULT_SIZE_METERS = 30;
const EDGE_HOVER_TOLERANCE_METERS = 10; // only show add button when within this distance to edge
const MIN_VERTEX_SEPARATION_METERS = 4;

const normalizePoint = (pt) => {
  if (!pt) return null;
  if (Array.isArray(pt) && pt.length >= 2) {
    return { lat: Number(pt[0]), lng: Number(pt[1]) };
  }
  if (typeof pt.lat === 'number' || typeof pt.lng === 'number') {
    return { lat: Number(pt.lat), lng: Number(pt.lng) };
  }
  if (typeof pt.latitude === 'number' || typeof pt.longitude === 'number') {
    return { lat: Number(pt.latitude), lng: Number(pt.longitude) };
  }
  return null;
};

/**
 * Polygon/zone renderer with draggable vertices. Adds vertices on click when editing.
 */
export default function PolygonShape({
  cp,
  setSelectedCheckpointId,
  updateCheckpoint,
  suppressFocusNext,
  handleIcon,
  metersPerDegreeLat,
  metersPerDegreeLng,
  editing = true,
  isZone = false,
  fillColor: overrideFillColor,
  strokeColor: overrideStrokeColor,
  fillOpacity: overrideFillOpacity,
  onVertexDragStart,
  onVertexDragEnd,
  onPolygonMouseDown,
  onPolygonMouseMove,
  onPolygonMouseUp,
  disableHoverAdd = false,
  allowVertexDrag = true,
  allowReshapeHandles = false,
  allowResizeHandles = false,
  allowAddVertex = true,
  eraseVertexMode = false,
  onEraseVertex,
  onVertexReshapeStart,
  onVertexResizeStart,
}) {
  const { geometry, commitGeometry } = useGeometryEditing({
    cp,
    updateCheckpoint,
    defaultType: 'polygon',
  });
  const [hoverAdd, setHoverAdd] = useState(null);
  const [suppressHoverAdd, setSuppressHoverAdd] = useState(false);
  const [freezeHoverAdd, setFreezeHoverAdd] = useState(false);
  const centerLat = Number(cp?.lat);
  const centerLng = Number(cp?.lng);
  const hasCenter = Number.isFinite(centerLat) && Number.isFinite(centerLng);
  const color = overrideStrokeColor || (isZone ? '#d9534f' : '#2e7be7');
  const fillColor = overrideFillColor || color;
  const fillOpacity = Number.isFinite(overrideFillOpacity) ? overrideFillOpacity : 0.15;
  const geometryType = isZone ? 'polygon' : geometry?.type || 'polygon';
  const persistPoints = (nextPoints) =>
    commitGeometry({
      type: geometryType,
      coord: nextPoints,
      points: nextPoints,
    });

  const points = useMemo(() => {
    const rawPoints = Array.isArray(geometry?.coord)
      ? geometry.coord
      : Array.isArray(geometry?.points)
        ? geometry.points
        : [];
    const normalized = Array.isArray(rawPoints)
      ? rawPoints.map(normalizePoint).filter(Boolean)
      : [];
    if (normalized.length) return normalized;
    if (!hasCenter) return [];
    const lngFactor = metersPerDegreeLng(centerLat || 0) || metersPerDegreeLat;
    const dLat = DEFAULT_SIZE_METERS / metersPerDegreeLat;
    const dLng = lngFactor ? DEFAULT_SIZE_METERS / lngFactor : 0;
    // Default to a small square.
    return [
      { lat: centerLat + dLat, lng: centerLng + dLng },
      { lat: centerLat + dLat, lng: centerLng - dLng },
      { lat: centerLat - dLat, lng: centerLng - dLng },
      { lat: centerLat - dLat, lng: centerLng + dLng },
    ];
  }, [
    centerLat,
    centerLng,
    geometry?.coord,
    geometry?.points,
    hasCenter,
    metersPerDegreeLat,
    metersPerDegreeLng,
  ]);

  const addVertexIcon = useMemo(
    () =>
      L.divIcon({
        className: 'polygon-add-vertex-icon',
        html: '<div class="polygon-add-vertex-dot">+</div>',
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      }),
    [],
  );

  const nearestPointOnEdge = useCallback(
    (latlng) => {
      if (!Array.isArray(points) || points.length < 2) return null;
      const lngFactor = metersPerDegreeLng(latlng.lat || 0) || metersPerDegreeLat;
      let best = null;
      points.forEach((pt, idx) => {
        const next = points[(idx + 1) % points.length];
        const dx = (next.lng - pt.lng) * lngFactor;
        const dy = (next.lat - pt.lat) * metersPerDegreeLat;
        const px = (latlng.lng - pt.lng) * lngFactor;
        const py = (latlng.lat - pt.lat) * metersPerDegreeLat;
        const denom = dx * dx + dy * dy || 1;
        const t = Math.max(0, Math.min(1, (px * dx + py * dy) / denom));
        const projLat = pt.lat + (next.lat - pt.lat) * t;
        const projLng = pt.lng + (next.lng - pt.lng) * t;
        const distX = px - dx * t;
        const distY = py - dy * t;
        const distSq = distX * distX + distY * distY;
        if (best == null || distSq < best.distSq) {
          best = {
            distSq,
            pos: { lat: projLat, lng: projLng },
            insertIndex: idx + 1,
          };
        }
      });
      if (best && best.distSq > EDGE_HOVER_TOLERANCE_METERS * EDGE_HOVER_TOLERANCE_METERS) {
        return null;
      }
      if (best) {
        const tooCloseToExisting = points.some((pt) => {
          const dx = (best.pos.lng - pt.lng) * lngFactor;
          const dy = (best.pos.lat - pt.lat) * metersPerDegreeLat;
          return dx * dx + dy * dy < MIN_VERTEX_SEPARATION_METERS * MIN_VERTEX_SEPARATION_METERS;
        });
        if (tooCloseToExisting) return null;
      }
      return best;
    },
    [metersPerDegreeLat, metersPerDegreeLng, points],
  );

  if (!points.length) return null;

  const handleVertexDrag = (idx) => ({
    drag: (e) => {
      if (!allowVertexDrag) return;
      suppressFocusNext.current = true;
      const next = points.slice();
      next[idx] = {
        lat: e.target.getLatLng().lat,
        lng: e.target.getLatLng().lng,
      };
      persistPoints(next);
    },
    dragstart: (e) => {
      if (!allowVertexDrag) return;
      if (typeof onVertexDragStart === 'function') onVertexDragStart(idx, e);
      else {
        const map = e?.target?._map;
        map?.dragging?.disable?.();
      }
    },
    dragend: (e) => {
      if (!allowVertexDrag) return;
      if (typeof onVertexDragEnd === 'function') onVertexDragEnd(idx, e);
      else {
        const map = e?.target?._map;
        map?.dragging?.enable?.();
      }
      suppressFocusNext.current = false;
    },
    click: () => {
      if (!allowVertexDrag && !eraseVertexMode) return;
      suppressFocusNext.current = true;
      if (eraseVertexMode && typeof onEraseVertex === 'function') {
        onEraseVertex(idx);
        return;
      }
      setSelectedCheckpointId(cp.id);
    },
  });

  const handlePolygonClick = (e) => {
    setSelectedCheckpointId(cp.id);
    if (!editing || !allowAddVertex) return;
    if (!hoverAdd) return;
    suppressFocusNext.current = true;
    handleAddVertex(hoverAdd.insertIndex, hoverAdd.pos);
  };

  const pathOptions = {
    color,
    fillColor,
    fillOpacity,
    weight: isZone ? 2 : 1,
    bubblingMouseEvents: false,
  };

  const handleAddVertex = (insertIndex, latlng) => {
    if (!hoverAdd) return;
    const lngFactor = metersPerDegreeLng(latlng.lat || 0) || metersPerDegreeLat;
    const tooCloseToExisting = points.some((pt) => {
      const dx = (latlng.lng - pt.lng) * lngFactor;
      const dy = (latlng.lat - pt.lat) * metersPerDegreeLat;
      return dx * dx + dy * dy < MIN_VERTEX_SEPARATION_METERS * MIN_VERTEX_SEPARATION_METERS;
    });
    if (tooCloseToExisting) return;
    suppressFocusNext.current = true;
    const next = points.slice();
    next.splice(insertIndex, 0, { lat: latlng.lat, lng: latlng.lng });
    persistPoints(next);
    setHoverAdd(null);
    setFreezeHoverAdd(true);
  };

  const handleMouseMove = (e) => {
    if (!editing || suppressHoverAdd || disableHoverAdd || !allowAddVertex) return;
    if (freezeHoverAdd) {
      setFreezeHoverAdd(false);
      return;
    }
    const nearest = nearestPointOnEdge(e.latlng);
    setHoverAdd(nearest ? { pos: nearest.pos, insertIndex: nearest.insertIndex } : null);
  };

  const handleMouseOut = () => {
    setHoverAdd(null);
    setFreezeHoverAdd(false);
  };

  return (
    <>
      <Polygon
        positions={points}
        pathOptions={pathOptions}
        bubblingMouseEvents={false}
        eventHandlers={{
          mousemove: (e) => {
            handleMouseMove(e);
            if (typeof onPolygonMouseMove === 'function') onPolygonMouseMove(e);
          },
          mouseover: (e) => {
            handleMouseMove(e);
          },
          mouseout: (e) => {
            handleMouseOut();
          },
          mousedown: (e) => {
            setFreezeHoverAdd(false);
            const originalEvent = e?.originalEvent;
            if (originalEvent) {
              L.DomEvent.preventDefault(originalEvent);
              L.DomEvent.stopPropagation(originalEvent);
            }
            if (typeof onPolygonMouseDown === 'function') onPolygonMouseDown(e);
          },
          mouseup: (e) => {
            if (typeof onPolygonMouseUp === 'function') onPolygonMouseUp(e);
          },
          click: handlePolygonClick,
        }}
      />
      {editing &&
        (allowVertexDrag ||
          allowReshapeHandles ||
          allowResizeHandles ||
          allowAddVertex ||
          eraseVertexMode ||
          !isZone) &&
        points.map((pt, idx) => (
          <Marker
            key={`polygon-vertex-${cp.id}-${idx}`}
            position={pt}
            draggable={(allowVertexDrag && !eraseVertexMode) || !isZone}
            interactive={
              allowVertexDrag ||
              allowReshapeHandles ||
              allowResizeHandles ||
              eraseVertexMode ||
              !isZone
            }
            icon={handleIcon}
            bubblingMouseEvents={false}
            eventHandlers={{
              ...handleVertexDrag(idx),
              mousedown: (e) => {
                e?.originalEvent?.stopPropagation?.();
                e?.originalEvent?.preventDefault?.();
                e?.originalEvent?.stopImmediatePropagation?.();
                if (eraseVertexMode && typeof onEraseVertex === 'function') {
                  onEraseVertex(idx);
                  return;
                }
                if (allowReshapeHandles && typeof onVertexReshapeStart === 'function') {
                  onVertexReshapeStart(idx, e);
                  return;
                }
                if (allowResizeHandles && typeof onVertexResizeStart === 'function') {
                  onVertexResizeStart(idx, e);
                }
              },
              click: (e) => {
                e?.originalEvent?.stopPropagation?.();
                e?.originalEvent?.preventDefault?.();
                if (allowReshapeHandles || allowResizeHandles) return;
                if (eraseVertexMode) return;
                handleVertexDrag(idx).click();
              },
              mouseover: () => {
                setSuppressHoverAdd(true);
                setHoverAdd(null);
              },
              mouseout: () => {
                setSuppressHoverAdd(false);
              },
            }}
          />
        ))}
      {editing && hoverAdd && allowAddVertex && !eraseVertexMode && (
        <Marker
          key={`polygon-hover-add-${cp.id}`}
          position={hoverAdd.pos}
          icon={addVertexIcon}
          eventHandlers={{
            mouseover: () => {
              if (!suppressHoverAdd) setHoverAdd(hoverAdd);
            },
            mouseout: handleMouseOut,
            click: () => {
              setSelectedCheckpointId(cp.id);
              handleAddVertex(hoverAdd.insertIndex, hoverAdd.pos);
            },
          }}
        >
          <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.9}>
            Click to add vertex
          </Tooltip>
        </Marker>
      )}
    </>
  );
}
